// External Libraries
import React from "react";
import moment from "moment";

// Services
import LoanReconciliationService from "../../../../services/LoanReconciliationService";
import BankReconciliationService from "../../../../services/BankReconciliationService";
import ClientService from "../../../../services/ClientService";

// Common Components
import WComponent from "../../../common/WComponent";
import Button from "../../../common/button/Button";
import DateSelector from "../../../common/date_selector/DateSelector";
import Loader from "../../../common/loader/Loader";

// Styling — reuse the bank reconciliation tool's CSS for layout, plus add a small file
import "../BankReconciliationTool/bank-reconciliation-tool.css";
import "./loan-reconciliation-tool.css";

const TABS = {
	summary: "summary",
	statementOnly: "statementOnly",
	glOnly: "glOnly",
	journalEntries: "journalEntries",
	schedule: "schedule",
	history: "history"
};

const STATUS_LABELS = {
	scheduled: "Scheduled",
	paid: "Paid",
	missed: "Missed",
	partial: "Partial",
	deferred: "Deferred",
	cancelled: "Cancelled",
	extra_paid: "Extra Paid",
	variance: "Variance"
};

class LoanReconciliationTool extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			// Input
			startDate: moment().startOf("month").toDate(),
			endDate: new Date(),
			selectedAccountId: null,
			selectedAccountName: "",
			cashAccountName: "",
			statementFile: null,

			// Data
			loanAccounts: [],
			bankAccounts: [],
			reconciliationResult: null,
			reconciliationHistory: [],
			clientName: "",

			// Schedule
			schedule: null,
			scheduleRows: [],
			scheduleHistory: [],
			scheduleMode: "upload", // "upload" | "generate"
			scheduleFile: null,
			genLoanAmount: "",
			genInterestRate: "",
			genTermMonths: "",
			genFirstPaymentDate: moment().add(1, "month").startOf("month").toDate(),
			genPaymentFrequency: "monthly",
			scheduleLoading: false,
			scheduleAction: null, // "upload" | "generate" | null (= passive fetch)
			scheduleStage: null,
			scheduleStartedAt: null,
			scheduleTick: 0,
			scheduleError: null,
			expandedRowId: null,
			rateChangeModalOpen: false,
			rateChangeEffectiveDate: new Date(),
			rateChangeNewRate: "",
			extraPaymentModalOpen: false,
			extraPaymentRowId: null,
			extraPaymentMode: "shorten",
			replaceModalOpen: false,
			replaceFile: null,
			replaceFromDate: new Date(),
			replaceSubmitting: false,
			replaceError: null,
			replaceSuccessToast: null,
			resetModalOpen: false,
			resetSubmitting: false,
			resetError: null,
			yearViewActive: false,
			yearViewYear: new Date().getFullYear(),
			yearOverview: null,

			// UI
			loading: false,
			reconcileStage: null,
			reconcileStartedAt: null,
			loadingAccounts: true,
			activeTab: TABS.summary,
			error: null,
			expandedDiffSection: null
		};
	}

	async componentDidMount() {
		const { clientId } = this.props;

		try {
			const [loanAccounts, bankAccounts, history, clientData] = await Promise.all([
				LoanReconciliationService.fetchLoanAccounts({ clientId }),
				BankReconciliationService.fetchBankAccounts({ clientId }),
				LoanReconciliationService.listReconciliations({ clientId }),
				ClientService.fetchClient({ clientId })
			]);

			await this.update({
				loanAccounts: loanAccounts || [],
				bankAccounts: bankAccounts || [],
				reconciliationHistory: history || [],
				clientName: clientData?.client?.name || "",
				loadingAccounts: false
			});
		} catch (err) {
			await this.update({ loadingAccounts: false, error: "Failed to load loan accounts" });
		}
	}

	// ── Input handlers ──

	onAccountChange = e => {
		const id = e.target.value;
		const acct = this.state.loanAccounts.find(a => String(a.id) === String(id));
		this.update({
			selectedAccountId: id,
			selectedAccountName: acct ? acct.name : ""
		});
		if (id) this.loadScheduleForAccount(id);
	};

	loadScheduleForAccount = async loanAccountId => {
		const { clientId } = this.props;
		await this.update({ scheduleLoading: true, scheduleError: null });
		const data = await LoanReconciliationService.getSchedule({ clientId, loanAccountId });
		await this.update({
			schedule: data?.schedule || null,
			scheduleRows: data?.rows || [],
			scheduleHistory: data?.history || [],
			scheduleLoading: false
		});
	};

	onCashAccountChange = e => {
		this.update({ cashAccountName: e.target.value });
	};

	onStartDateChange = date => this.update({ startDate: date });
	onEndDateChange = date => this.update({ endDate: date });

	onFileChange = e => {
		const file = e.target.files[0];
		if (file) this.update({ statementFile: file });
	};

	onFileRemove = () => {
		this.update({ statementFile: null });
		if (this.fileInputRef) this.fileInputRef.value = "";
	};

	onReconcileClicked = async () => {
		const {
			selectedAccountId,
			selectedAccountName,
			startDate,
			endDate,
			statementFile,
			cashAccountName
		} = this.state;
		const { clientId } = this.props;

		if (!selectedAccountId) return this.update({ error: "Select a loan account first" });
		if (!statementFile) return this.update({ error: "Upload a loan statement PDF" });

		await this.update({
			loading: true,
			error: null,
			reconciliationResult: null,
			reconcileStage: 0,
			reconcileStartedAt: Date.now(),
			reconcileTick: 0
		});

		// 1-second ticker to keep "elapsed" counter fresh in the UI.
		this._tickInterval = setInterval(() => {
			if (this.state.loading) this.update({ reconcileTick: (this.state.reconcileTick || 0) + 1 });
			else clearInterval(this._tickInterval);
		}, 1000);

		// Stage progression. The actual API call runs in parallel — these
		// timers just advance the UI through realistic phases so the user
		// has visibility into what's happening. If the API returns before
		// the last stage, we jump straight to done.
		const STAGE_TIMINGS_MS = [4500, 4000, 2500, 2500]; // cumulative ≈ 13.5s
		this._stageTimers = [];
		let cumulative = 0;
		STAGE_TIMINGS_MS.forEach((ms, idx) => {
			cumulative += ms;
			const t = setTimeout(() => {
				// Don't advance past the last "in-progress" stage; the
				// completion handler below sets it to STAGE_COUNT.
				if (this.state.loading) this.update({ reconcileStage: idx + 1 });
			}, cumulative);
			this._stageTimers.push(t);
		});

		const clearStageTimers = () => {
			(this._stageTimers || []).forEach(clearTimeout);
			this._stageTimers = [];
		};

		const result = await LoanReconciliationService.runReconciliation({
			clientId,
			accountId: selectedAccountId,
			accountName: selectedAccountName,
			startDate: moment(startDate).format("YYYY-MM-DD"),
			endDate: moment(endDate).format("YYYY-MM-DD"),
			file: statementFile,
			cashAccountName: cashAccountName || "Cash"
		});

		clearStageTimers();
		if (this._tickInterval) {
			clearInterval(this._tickInterval);
			this._tickInterval = null;
		}

		if (result.error) {
			await this.update({ loading: false, reconcileStage: null, error: result.error });
			return;
		}

		const refreshedHistory = await LoanReconciliationService.listReconciliations({ clientId });

		await this.update({
			loading: false,
			reconcileStage: null,
			reconciliationResult: result,
			reconciliationHistory: refreshedHistory || [],
			activeTab: TABS.summary
		});

		// Refresh the amortization schedule so the Schedule tab reflects the
		// statuses just persisted by this reconciliation. Without this, the
		// schedule tab keeps showing whatever was loaded when the loan account
		// was first selected — so a row that just flipped to MISSED/VARIANCE/
		// PAID still renders with its prior label until the user navigates
		// away and back.
		await this.loadScheduleForAccount(selectedAccountId);
	};

	onTabClick = tab => this.update({ activeTab: tab });

	onToggleDiffSection = key => {
		this.update({ expandedDiffSection: this.state.expandedDiffSection === key ? null : key });
	};

	// ── JE actions (reuse the bank reconciliation endpoints — same table, type-agnostic) ──

	_updateAdjustmentInState = (adjustmentId, newStatus, extraData) => {
		const { reconciliationResult } = this.state;
		if (!reconciliationResult) return;
		const updated = {
			...reconciliationResult,
			journalEntrySuggestions: reconciliationResult.journalEntrySuggestions.map(je =>
				je.id === adjustmentId ? { ...je, status: newStatus, ...(extraData || {}) } : je
			)
		};
		this.update({ reconciliationResult: updated });
	};

	onAcceptAdjustment = async adjustmentId => {
		const result = await BankReconciliationService.updateAdjustmentStatus({
			adjustmentId,
			status: "accepted"
		});
		if (result && !result.error) this._updateAdjustmentInState(adjustmentId, "accepted");
	};

	onRejectAdjustment = async adjustmentId => {
		const result = await BankReconciliationService.updateAdjustmentStatus({
			adjustmentId,
			status: "rejected"
		});
		if (result && !result.error) this._updateAdjustmentInState(adjustmentId, "rejected");
	};

	// Local-only edit: updates the field in state on every keystroke so the
	// input stays controlled. Persistence happens on blur via
	// onCommitAdjustmentField below — that avoids a network call per keystroke.
	onChangeAdjustmentField = (adjustmentId, field, value) => {
		const { reconciliationResult } = this.state;
		if (!reconciliationResult) return;
		this.update({
			reconciliationResult: {
				...reconciliationResult,
				journalEntrySuggestions: reconciliationResult.journalEntrySuggestions.map(je =>
					je.id === adjustmentId ? { ...je, [field]: value } : je
				)
			}
		});
	};

	onCommitAdjustmentField = async (adjustmentId, field, value) => {
		// Only memo and txnDate are user-editable.
		const payload = { adjustmentId };
		if (field === "memo") payload.memo = value || "";
		else if (field === "txnDate") payload.txnDate = value || null;
		else return;

		const result = await BankReconciliationService.updateAdjustmentStatus(payload);
		if (!result || result.error) {
			this.update({ error: (result && result.error) || "Failed to save change" });
		}
	};

	onPostAdjustment = async adjustmentId => {
		const { clientId } = this.props;
		this._updateAdjustmentInState(adjustmentId, "posting");

		const result = await BankReconciliationService.postAdjustmentToQBO({
			adjustmentId,
			clientId
		});

		if (result && !result.error) {
			let postedDebitAccount = null;
			let postedCreditAccount = null;
			if (result.journalEntry && result.journalEntry.Line) {
				result.journalEntry.Line.forEach(line => {
					const detail = line.JournalEntryLineDetail;
					if (detail && detail.AccountRef) {
						if (detail.PostingType === "Debit") postedDebitAccount = detail.AccountRef.name;
						else if (detail.PostingType === "Credit") postedCreditAccount = detail.AccountRef.name;
					}
				});
			}
			this._updateAdjustmentInState(adjustmentId, "posted", { postedDebitAccount, postedCreditAccount });
		} else {
			this._updateAdjustmentInState(adjustmentId, "accepted");
			this.update({ error: result.error || "Failed to post journal entry" });
		}
	};

	// ── Helpers ──

	formatCurrency(amount, currencyCode) {
		const cur = (currencyCode || "CAD").toUpperCase();
		try {
			return new Intl.NumberFormat("en-CA", {
				style: "currency",
				currency: cur,
				currencyDisplay: "code"
			}).format(amount || 0);
		} catch (e) {
			return `${cur} ${(amount || 0).toFixed(2)}`;
		}
	}

	// ── Render ──

	renderScheduleImportProgress() {
		const { scheduleAction, scheduleFile } = this.state;

		// Passive load (e.g. switching loan accounts) — keep it minimal,
		// the request usually returns in <1 second.
		if (scheduleAction == null) {
			return (
				<div className="recon-progress recon-progress--small">
					<div className="recon-progress__header">
						<div className="recon-progress__title">Loading schedule…</div>
					</div>
				</div>
			);
		}

		if (scheduleAction === "generate") {
			return (
				<div className="recon-progress recon-progress--small">
					<div className="recon-progress__header">
						<div className="recon-progress__title">Generating schedule…</div>
						<div className="recon-progress__sub">Computing every row from your loan terms.</div>
					</div>
				</div>
			);
		}

		// scheduleAction === "upload"
		const isPdf = scheduleFile && (scheduleFile.name || "").toLowerCase().endsWith(".pdf");
		const STAGES = isPdf
			? [
				{ label: "Reading PDF", detail: "Adobe Document Services extracts every line." },
				{ label: "Extracting rows with AI", detail: "Identifying payment number, dates, principal, interest, balance." },
				{ label: "Saving schedule rows", detail: "Persisting your amortization plan to the database." }
			]
			: [
				{ label: "Parsing file", detail: "Reading column headers and row data." },
				{ label: "Mapping fields", detail: "Detecting Date / Principal / Interest / Balance columns." },
				{ label: "Saving schedule rows", detail: "Persisting your amortization plan to the database." }
			];

		const current = this.state.scheduleStage == null ? 0 : this.state.scheduleStage;
		const elapsedSec = this.state.scheduleStartedAt
			? Math.max(0, Math.round((Date.now() - this.state.scheduleStartedAt) / 1000))
			: 0;

		return (
			<div className="recon-progress">
				<div className="recon-progress__header">
					<div className="recon-progress__title">Importing schedule…</div>
					<div className="recon-progress__sub">
						{isPdf
							? `PDF parsing usually takes 10–15 seconds. ${elapsedSec}s elapsed.`
							: `Should be ready in a couple of seconds. ${elapsedSec}s elapsed.`}
					</div>
				</div>
				<ol className="recon-progress__steps">
					{STAGES.map((s, idx) => {
						const state =
							idx < current ? "done" :
							idx === current ? "active" :
							"pending";
						return (
							<li key={idx} className={`recon-progress__step recon-progress__step--${state}`}>
								<span className="recon-progress__step-marker">
									{state === "done" ? "✓" : state === "active" ? <span className="recon-progress__step-spinner" /> : idx + 1}
								</span>
								<div className="recon-progress__step-text">
									<div className="recon-progress__step-label">{s.label}</div>
									<div className="recon-progress__step-detail">{s.detail}</div>
								</div>
							</li>
						);
					})}
				</ol>
			</div>
		);
	}

	renderReconcileProgress() {
		const STAGES = [
			{ label: "Reading statement PDF", detail: "Adobe extract + AI parser pull every transaction." },
			{ label: "Fetching journal entries from QBO", detail: "Pulling every JE touching the loan account in the period." },
			{ label: "Matching transactions", detail: "Pairing statement items with GL entries by amount + account type." },
			{ label: "Reconciling schedule rows", detail: "Walking the amortization schedule against actual payments." }
		];
		const current = this.state.reconcileStage == null ? 0 : this.state.reconcileStage;
		const elapsedSec = this.state.reconcileStartedAt
			? Math.max(0, Math.round((Date.now() - this.state.reconcileStartedAt) / 1000))
			: 0;

		return (
			<div className="recon-progress">
				<div className="recon-progress__header">
					<div className="recon-progress__title">Reconciling…</div>
					<div className="recon-progress__sub">
						This usually takes 10–20 seconds. {elapsedSec}s elapsed.
					</div>
				</div>
				<ol className="recon-progress__steps">
					{STAGES.map((s, idx) => {
						const state =
							idx < current ? "done" :
							idx === current ? "active" :
							"pending";
						return (
							<li key={idx} className={`recon-progress__step recon-progress__step--${state}`}>
								<span className="recon-progress__step-marker">
									{state === "done" ? "✓" : state === "active" ? <span className="recon-progress__step-spinner" /> : idx + 1}
								</span>
								<div className="recon-progress__step-text">
									<div className="recon-progress__step-label">{s.label}</div>
									<div className="recon-progress__step-detail">{s.detail}</div>
								</div>
							</li>
						);
					})}
				</ol>
			</div>
		);
	}

	renderControls() {
		const {
			loanAccounts,
			selectedAccountId,
			startDate,
			endDate,
			loadingAccounts,
			statementFile,
			loading,
			cashAccountName,
			bankAccounts
		} = this.state;

		return (
			<div className="bank-recon-tool__controls-wrap">
				<div className="bank-recon-tool__controls">
					<div className="bank-recon-tool__account-select">
						<label>Loan Account (Books)</label>
						<select value={selectedAccountId || ""} onChange={this.onAccountChange} disabled={loadingAccounts}>
							<option value="">{loadingAccounts ? "Loading loan accounts..." : "Select a QBO loan account"}</option>
							{loanAccounts.map(account => (
								<option key={account.id} value={account.id}>
									{account.name} ({this.formatCurrency(account.currentBalance, account.currency)})
								</option>
							))}
						</select>
					</div>

					<DateSelector
						title="Period Start"
						selectedDate={startDate ? new Date(startDate) : undefined}
						onDateSelect={this.onStartDateChange}
					/>
					<DateSelector
						title="Period End"
						selectedDate={endDate ? new Date(endDate) : undefined}
						onDateSelect={this.onEndDateChange}
					/>
				</div>

				<div className="bank-recon-tool__controls">
					<div className="bank-recon-tool__file-upload">
						<label>Loan Statement (PDF)</label>
						<div className="bank-recon-tool__file-upload-row">
							<label className="bank-recon-tool__file-btn" htmlFor="loan-statement-upload">
								{statementFile ? "Change File" : "Upload PDF"}
								<input
									id="loan-statement-upload"
									type="file"
									accept=".pdf"
									onChange={this.onFileChange}
									ref={ref => (this.fileInputRef = ref)}
									style={{ display: "none" }}
								/>
							</label>
							{statementFile && (
								<div className="bank-recon-tool__file-info">
									<span className="bank-recon-tool__file-name">{statementFile.name}</span>
									<span className="bank-recon-tool__file-remove" onClick={this.onFileRemove}>
										&times;
									</span>
								</div>
							)}
						</div>
					</div>

					<div className="bank-recon-tool__account-select">
						<label>Cash / Bank Account (for JE offsets)</label>
						<select value={cashAccountName || ""} onChange={this.onCashAccountChange}>
							<option value="">Select cash/bank account</option>
							{bankAccounts.map(a => (
								<option key={a.id} value={a.name}>
									{a.name}
								</option>
							))}
						</select>
					</div>

					<Button text="Reconcile Loan" onClick={this.onReconcileClicked} disabled={loading} />
				</div>
			</div>
		);
	}

	renderTabs() {
		const { activeTab, reconciliationResult, selectedAccountId, schedule } = this.state;
		if (!reconciliationResult && !selectedAccountId) return null;

		const stmtCount = reconciliationResult?.unmatchedStatementItems?.length || 0;
		const glCount = reconciliationResult?.unmatchedGLItems?.length || 0;
		const jeCount = reconciliationResult?.journalEntrySuggestions?.length || 0;
		const showAfterRun = !!reconciliationResult;
		// Schedule tab is visible (a) post-recon so the user can inspect the
		// updated statuses, OR (b) before any schedule exists so the user can
		// still reach the "Set up an amortization schedule" screen. Once a
		// schedule is set up but no recon has been run yet, we hide it to
		// keep the pre-recon UI clean.
		const showScheduleTab = showAfterRun || !schedule;
		// History tab only shows after the user runs a reconciliation this
		// session — we deliberately don't surface it based on past history
		// alone, so the pre-recon UI stays focused on the act of running.
		const showHistoryTab = showAfterRun;

		return (
			<div className="bank-recon-tabs">
				{showAfterRun && (
					<div className={`bank-recon-tabs__tab ${activeTab === TABS.summary ? "bank-recon-tabs__tab--active" : ""}`} onClick={() => this.onTabClick(TABS.summary)}>
						Summary
					</div>
				)}
				{showAfterRun && (
					<div className={`bank-recon-tabs__tab ${activeTab === TABS.statementOnly ? "bank-recon-tabs__tab--active" : ""}`} onClick={() => this.onTabClick(TABS.statementOnly)}>
						Statement-Only Items ({stmtCount})
					</div>
				)}
				{showAfterRun && (
					<div className={`bank-recon-tabs__tab ${activeTab === TABS.glOnly ? "bank-recon-tabs__tab--active" : ""}`} onClick={() => this.onTabClick(TABS.glOnly)}>
						GL-Only Items ({glCount})
					</div>
				)}
				{showAfterRun && (
					<div className={`bank-recon-tabs__tab ${activeTab === TABS.journalEntries ? "bank-recon-tabs__tab--active" : ""}`} onClick={() => this.onTabClick(TABS.journalEntries)}>
						Suggested JEs ({jeCount})
					</div>
				)}
				{showScheduleTab && (
					<div className={`bank-recon-tabs__tab ${activeTab === TABS.schedule ? "bank-recon-tabs__tab--active" : ""}`} onClick={() => this.onTabClick(TABS.schedule)}>
						Schedule{this.state.schedule ? ` (${this.state.scheduleRows.length})` : ""}
					</div>
				)}
				{showHistoryTab && (
					<div className={`bank-recon-tabs__tab ${activeTab === TABS.history ? "bank-recon-tabs__tab--active" : ""}`} onClick={() => this.onTabClick(TABS.history)}>
						History
					</div>
				)}
			</div>
		);
	}

	renderSummary() {
		const { reconciliationResult, expandedDiffSection } = this.state;
		if (!reconciliationResult) return null;
		const { summary, matchingStats, statementMeta, unmatchedStatementItems = [] } = reconciliationResult;

		const diffZero = Math.abs(summary.difference || 0) < 0.005;

		const itemsByCategory = key => unmatchedStatementItems.filter(i => i.category === key);

		const lineRow = (label, amount, key, items) => {
			const expanded = expandedDiffSection === key;
			const clickable = items && items.length > 0;
			return (
				<>
					<div
						className={`loan-recon__row ${clickable ? "loan-recon__row--clickable" : ""}`}
						onClick={clickable ? () => this.onToggleDiffSection(key) : undefined}
					>
						<span className="loan-recon__row-label">
							{clickable && (expanded ? "▾ " : "▸ ")}
							{label}
						</span>
						<span className="loan-recon__row-amount">{this.formatCurrency(amount)}</span>
					</div>
					{expanded && (
						<div className="loan-recon__row-detail">
							<table className="loan-recon__detail-table">
								<thead>
									<tr><th>Date</th><th>Description</th><th>Amount</th></tr>
								</thead>
								<tbody>
									{items.map((item, idx) => (
										<tr key={idx}>
											<td>{item.date}</td>
											<td>{item.description || item.name}</td>
											<td>{this.formatCurrency(item.amount)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</>
			);
		};

		return (
			<>
				<div className="bank-recon-stats">
					<div className="bank-recon-stats__badge">
						<div className="bank-recon-stats__badge-label">Statement items</div>
						<div className="bank-recon-stats__badge-value">{matchingStats.totalStatementItems}</div>
					</div>
					<div className="bank-recon-stats__badge">
						<div className="bank-recon-stats__badge-label">GL entries</div>
						<div className="bank-recon-stats__badge-value">{matchingStats.totalGLEntries}</div>
					</div>
					<div className="bank-recon-stats__badge">
						<div className="bank-recon-stats__badge-label">Exact matches</div>
						<div className="bank-recon-stats__badge-value">{matchingStats.exactMatches}</div>
					</div>
					<div className="bank-recon-stats__badge">
						<div className="bank-recon-stats__badge-label">Fuzzy matches</div>
						<div className="bank-recon-stats__badge-value">{matchingStats.fuzzyMatches}</div>
					</div>
					<div className="bank-recon-stats__badge">
						<div className="bank-recon-stats__badge-label">AI matches</div>
						<div className="bank-recon-stats__badge-value">{matchingStats.aiMatches}</div>
					</div>
				</div>

				<div className="loan-recon__sides">
					<div className="loan-recon__side">
						<h3 className="loan-recon__side-title">Per Lender Statement</h3>
						{lineRow("Opening Principal Balance", summary.openingPrincipal, "opening", null)}
						{lineRow("+ Disbursements", summary.disbursements, "disbursements", itemsByCategory("disbursement"))}
						{lineRow("+ Interest Accrued", summary.interestAccrued, "interest_accrued", itemsByCategory("interest_charge").filter(i => i.loanComponent === "interest_accrual"))}
						{lineRow("+ Late Fees", summary.lateFees, "late_fees", itemsByCategory("late_fee"))}
						{lineRow("− Principal Payments", summary.principalPayments, "principal_payments", itemsByCategory("principal_payment"))}
						{lineRow("− Interest Paid", summary.interestPaid, "interest_paid", itemsByCategory("interest_charge").filter(i => i.loanComponent === "interest"))}
						{lineRow("− Escrow Paid", summary.escrowPaid, "escrow", itemsByCategory("escrow_payment"))}
						<div className="loan-recon__row loan-recon__row--total">
							<span className="loan-recon__row-label">= Closing Per Lender</span>
							<span className="loan-recon__row-amount">{this.formatCurrency(summary.closingPerLender)}</span>
						</div>
					</div>

					<div className="loan-recon__side">
						<h3 className="loan-recon__side-title">Per Books (GL)</h3>
						<div className="loan-recon__row">
							<span className="loan-recon__row-label">Closing GL Balance</span>
							<span className="loan-recon__row-amount">{this.formatCurrency(summary.glClosingBalance)}</span>
						</div>
						<div className="loan-recon__note">
							Pulled from the QBO trial balance for this loan account at period end.
						</div>
					</div>
				</div>

				<div className={`loan-recon__diff ${diffZero ? "loan-recon__diff--zero" : "loan-recon__diff--nonzero"}`}>
					<div className="loan-recon__diff-label">
						{diffZero ? "✓ Reconciled" : "⚠ Unreconciled difference"}
					</div>
					<div className="loan-recon__diff-amount">{this.formatCurrency(summary.difference)}</div>
					{!diffZero && (
						<div className="loan-recon__diff-help">
							Statement closing principal differs from GL by this amount. Review Statement-Only Items
							and the Suggested JEs tab to clear the difference.
						</div>
					)}
				</div>

				{statementMeta && statementMeta.statementPeriod && (
					<div className="loan-recon__meta">
						Statement period: {statementMeta.statementPeriod.startDate} to {statementMeta.statementPeriod.endDate}
					</div>
				)}
			</>
		);
	}

	renderItemsTable(items, title, sourceLabel) {
		if (!items || items.length === 0) {
			return (
				<div className="bank-recon-items">
					<h3>{title}</h3>
					<p className="bank-recon-items__empty">No items.</p>
				</div>
			);
		}

		return (
			<div className="bank-recon-items">
				<h3>{title}</h3>
				<table className="bank-recon-items__table">
					<thead>
						<tr>
							<th>Date</th>
							<th>Description</th>
							<th>Component</th>
							<th>Amount</th>
							<th>Category</th>
						</tr>
					</thead>
					<tbody>
						{items.map((item, idx) => (
							<tr key={idx}>
								<td>{item.date}</td>
								<td>{item.description || item.name}</td>
								<td>{item.loanComponent || sourceLabel || "-"}</td>
								<td>{this.formatCurrency(item.amount)}</td>
								<td>
									<span className={`bank-recon-items__category bank-recon-items__category--${item.category}`}>
										{(item.category || "").replace(/_/g, " ")}
									</span>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	renderJournalEntries() {
		const { reconciliationResult } = this.state;
		const suggestions = reconciliationResult?.journalEntrySuggestions || [];

		if (suggestions.length === 0) {
			return (
				<div className="bank-recon-je">
					<h3>Suggested Adjusting Journal Entries</h3>
					<p>No journal entries to suggest.</p>
				</div>
			);
		}

		return (
			<div className="bank-recon-je">
				<h3>Suggested Adjusting Journal Entries</h3>
				<table className="bank-recon-items__table">
					<thead>
						<tr>
							<th>Debit Account</th>
							<th>Credit Account</th>
							<th>Amount</th>
							<th>Posting Date</th>
							<th>Memo</th>
							<th>Status</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{suggestions.map((je, idx) => {
							const editable = je.status === "suggested" || je.status === "accepted";
							return (
							<tr key={je.id || idx}>
								<td>{je.debitAccount}</td>
								<td>{je.creditAccount}</td>
								<td>{this.formatCurrency(je.amount)}</td>
								<td>
									{editable ? (
										<input
											type="date"
											className="bank-recon-je__date-input"
											value={je.txnDate || ""}
											onChange={e => this.onChangeAdjustmentField(je.id, "txnDate", e.target.value)}
											onBlur={e => this.onCommitAdjustmentField(je.id, "txnDate", e.target.value)}
										/>
									) : (
										je.txnDate || "—"
									)}
								</td>
								<td>
									{editable ? (
										<input
											type="text"
											className="bank-recon-je__memo-input"
											value={je.memo || ""}
											onChange={e => this.onChangeAdjustmentField(je.id, "memo", e.target.value)}
											onBlur={e => this.onCommitAdjustmentField(je.id, "memo", e.target.value)}
										/>
									) : (
										je.memo
									)}
								</td>
								<td>
									<span className={`bank-recon-je__status bank-recon-je__status--${je.status}`}>
										{je.status}
									</span>
								</td>
								<td>
									<div className="bank-recon-je__actions">
										{je.status === "suggested" && (
											<>
												<button
													className="bank-recon-je__btn bank-recon-je__btn--accept"
													onClick={() => this.onAcceptAdjustment(je.id)}
												>
													Accept
												</button>
												<button
													className="bank-recon-je__btn bank-recon-je__btn--reject"
													onClick={() => this.onRejectAdjustment(je.id)}
												>
													Reject
												</button>
											</>
										)}
										{je.status === "accepted" && (
											<button
												className="bank-recon-je__btn bank-recon-je__btn--post"
												onClick={() => this.onPostAdjustment(je.id)}
											>
												Post to QBO
											</button>
										)}
										{je.status === "posting" && (
											<span className="bank-recon-je__posting">Posting...</span>
										)}
										{je.status === "posted" && (
											<div className="bank-recon-je__posted-info">
												<span className="bank-recon-je__posted">Posted</span>
												{(je.postedDebitAccount || je.postedCreditAccount) && (
													<span className="bank-recon-je__posted-accounts">
														DR: {je.postedDebitAccount || je.debitAccount} / CR: {je.postedCreditAccount || je.creditAccount}
													</span>
												)}
											</div>
										)}
										{je.status === "rejected" && (
											<span className="bank-recon-je__rejected">Rejected</span>
										)}
									</div>
								</td>
							</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		);
	}

	renderHistory() {
		const { reconciliationHistory } = this.state;

		if (!reconciliationHistory || reconciliationHistory.length === 0) {
			return (
				<div className="bank-recon-history">
					<h3>History</h3>
					<p>No previous loan reconciliations.</p>
				</div>
			);
		}

		return (
			<div className="bank-recon-history">
				<h3>History</h3>
				<table className="bank-recon-items__table">
					<thead>
						<tr>
							<th>Account</th>
							<th>Period</th>
							<th>Difference</th>
							<th>Status</th>
							<th>Created</th>
						</tr>
					</thead>
					<tbody>
						{reconciliationHistory.map(r => (
							<tr key={r.id}>
								<td>{r.account_name}</td>
								<td>{r.period_start} → {r.period_end}</td>
								<td>{this.formatCurrency(parseFloat(r.difference) || 0)}</td>
								<td>{r.status}</td>
								<td>{moment(r.created_at).format("MMM D, YYYY h:mm A")}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	// ── Schedule actions ──

	onScheduleModeChange = mode => this.update({ scheduleMode: mode, scheduleError: null });
	onScheduleFileChange = e => {
		const f = e.target.files[0];
		if (f) this.update({ scheduleFile: f });
	};
	onReplaceFileChange = e => {
		const f = e.target.files[0];
		if (f) this.update({ replaceFile: f });
	};

	onUploadSchedule = async () => {
		const { selectedAccountId, selectedAccountName, scheduleFile } = this.state;
		const { clientId } = this.props;
		if (!selectedAccountId) return this.update({ scheduleError: "Select a loan account first" });
		if (!scheduleFile) return this.update({ scheduleError: "Choose a file" });

		const isPdf = (scheduleFile.name || "").toLowerCase().endsWith(".pdf");
		await this.update({
			scheduleLoading: true,
			scheduleError: null,
			scheduleAction: "upload",
			scheduleStage: 0,
			scheduleStartedAt: Date.now(),
			scheduleTick: 0
		});

		// PDFs go through Adobe + AI which takes 10-15s; XLSX/CSV finish in
		// well under a second, so we use a shorter timing model there.
		const stageMs = isPdf ? [5500, 5000, 2000] : [400, 400, 200];
		this._scheduleStageTimers = [];
		let cum = 0;
		stageMs.forEach((ms, idx) => {
			cum += ms;
			const t = setTimeout(() => {
				if (this.state.scheduleLoading) this.update({ scheduleStage: idx + 1 });
			}, cum);
			this._scheduleStageTimers.push(t);
		});
		this._scheduleTickInterval = setInterval(() => {
			if (this.state.scheduleLoading) this.update({ scheduleTick: (this.state.scheduleTick || 0) + 1 });
			else clearInterval(this._scheduleTickInterval);
		}, 1000);

		const result = await LoanReconciliationService.uploadSchedule({
			clientId,
			loanAccountId: selectedAccountId,
			loanAccountName: selectedAccountName,
			file: scheduleFile
		});

		(this._scheduleStageTimers || []).forEach(clearTimeout);
		this._scheduleStageTimers = [];
		if (this._scheduleTickInterval) {
			clearInterval(this._scheduleTickInterval);
			this._scheduleTickInterval = null;
		}

		if (result.error) {
			await this.update({
				scheduleLoading: false,
				scheduleAction: null,
				scheduleStage: null,
				scheduleError: result.error
			});
			return;
		}
		await this.update({
			schedule: result.schedule,
			scheduleRows: result.rows || [],
			scheduleHistory: result.history || [],
			scheduleFile: null,
			scheduleLoading: false,
			scheduleAction: null,
			scheduleStage: null
		});
	};

	onGenerateSchedule = async () => {
		const {
			selectedAccountId,
			selectedAccountName,
			genLoanAmount,
			genInterestRate,
			genTermMonths,
			genFirstPaymentDate,
			genPaymentFrequency
		} = this.state;
		const { clientId } = this.props;
		if (!selectedAccountId) return this.update({ scheduleError: "Select a loan account first" });
		if (!genLoanAmount || !genInterestRate || !genTermMonths || !genFirstPaymentDate) {
			return this.update({ scheduleError: "Fill in all loan term fields" });
		}
		await this.update({
			scheduleLoading: true,
			scheduleError: null,
			scheduleAction: "generate"
		});
		const result = await LoanReconciliationService.generateSchedule({
			clientId,
			loanAccountId: selectedAccountId,
			loanAccountName: selectedAccountName,
			loanAmount: parseFloat(genLoanAmount),
			interestRate: parseFloat(genInterestRate),
			termMonths: parseInt(genTermMonths),
			firstPaymentDate: moment(genFirstPaymentDate).format("YYYY-MM-DD"),
			paymentFrequency: genPaymentFrequency
		});
		if (result.error) {
			await this.update({ scheduleLoading: false, scheduleAction: null, scheduleError: result.error });
			return;
		}
		await this.update({
			schedule: result.schedule,
			scheduleRows: result.rows || [],
			scheduleHistory: result.history || [],
			scheduleLoading: false,
			scheduleAction: null
		});
	};

	onMarkRowDeferred = async rowId => {
		const result = await LoanReconciliationService.updateScheduleRow({
			rowId,
			status: "deferred",
			notes: "Marked deferred from Schedule tab."
		});
		if (!result.error) {
			this.update({
				scheduleRows: this.state.scheduleRows.map(r =>
					r.id === rowId ? { ...r, status: "deferred", notes: "Marked deferred from Schedule tab." } : r
				)
			});
		}
	};

	onRevertRowToScheduled = async rowId => {
		const result = await LoanReconciliationService.updateScheduleRow({
			rowId,
			status: "scheduled",
			notes: ""
		});
		if (!result.error) {
			this.update({
				scheduleRows: this.state.scheduleRows.map(r =>
					r.id === rowId ? { ...r, status: "scheduled", notes: "" } : r
				)
			});
		}
	};

	onOpenRateChangeModal = () => this.update({ rateChangeModalOpen: true });
	onCloseRateChangeModal = () => this.update({ rateChangeModalOpen: false });

	onSubmitRateChange = async () => {
		const { schedule, rateChangeEffectiveDate, rateChangeNewRate } = this.state;
		if (!schedule || !rateChangeNewRate) return;
		await this.update({ scheduleLoading: true, scheduleError: null });
		const result = await LoanReconciliationService.recalculateSchedule({
			action: "rate_change",
			scheduleId: schedule.id,
			effectiveDate: moment(rateChangeEffectiveDate).format("YYYY-MM-DD"),
			newRate: parseFloat(rateChangeNewRate)
		});
		if (result.error) {
			await this.update({ scheduleLoading: false, scheduleError: result.error });
			return;
		}
		await this.update({
			schedule: result.schedule,
			scheduleRows: result.rows || [],
			scheduleHistory: result.history || [],
			scheduleLoading: false,
			rateChangeModalOpen: false,
			rateChangeNewRate: ""
		});
	};

	onOpenExtraPayment = rowId => this.update({ extraPaymentModalOpen: true, extraPaymentRowId: rowId, extraPaymentMode: "shorten" });
	onCloseExtraPayment = () => this.update({ extraPaymentModalOpen: false, extraPaymentRowId: null });

	onSubmitExtraPayment = async () => {
		const { schedule, extraPaymentRowId, extraPaymentMode } = this.state;
		if (!schedule || !extraPaymentRowId) return;
		await this.update({ scheduleLoading: true, scheduleError: null });
		const result = await LoanReconciliationService.recalculateSchedule({
			action: "extra_payment",
			scheduleId: schedule.id,
			fromRowId: extraPaymentRowId,
			mode: extraPaymentMode
		});
		if (result.error) {
			await this.update({ scheduleLoading: false, scheduleError: result.error });
			return;
		}
		await this.update({
			schedule: result.schedule,
			scheduleRows: result.rows || [],
			scheduleHistory: result.history || [],
			scheduleLoading: false,
			extraPaymentModalOpen: false,
			extraPaymentRowId: null
		});
	};

	onOpenReplace = () =>
		this.update({ replaceModalOpen: true, replaceError: null, replaceSubmitting: false });
	onCloseReplace = () => {
		if (this.state.replaceSubmitting) return; // don't close while uploading
		this.update({ replaceModalOpen: false, replaceError: null });
	};

	onSubmitReplace = async () => {
		const { selectedAccountId, selectedAccountName, replaceFile, replaceFromDate } = this.state;
		const { clientId } = this.props;
		if (!replaceFile) return this.update({ replaceError: "Choose a file to upload first." });

		await this.update({ replaceSubmitting: true, replaceError: null });

		const result = await LoanReconciliationService.replaceSchedule({
			clientId,
			loanAccountId: selectedAccountId,
			loanAccountName: selectedAccountName,
			file: replaceFile,
			replaceFromDate: moment(replaceFromDate).format("YYYY-MM-DD")
		});

		if (result.error) {
			await this.update({ replaceSubmitting: false, replaceError: result.error });
			return;
		}

		const newRowCount = (result.rows || []).length;
		await this.update({
			schedule: result.schedule,
			scheduleRows: result.rows || [],
			scheduleHistory: result.history || [],
			replaceSubmitting: false,
			replaceModalOpen: false,
			replaceFile: null,
			replaceSuccessToast: `Schedule replaced — ${newRowCount} new payment${newRowCount === 1 ? "" : "s"} scheduled.`
		});

		// Auto-dismiss the success toast after 5 seconds.
		if (this._toastTimer) clearTimeout(this._toastTimer);
		this._toastTimer = setTimeout(
			() => this.update({ replaceSuccessToast: null }),
			5000
		);
	};

	onDismissReplaceToast = () => {
		if (this._toastTimer) clearTimeout(this._toastTimer);
		this.update({ replaceSuccessToast: null });
	};

	onOpenResetModal = () =>
		this.update({ resetModalOpen: true, resetError: null, resetSubmitting: false });
	onCloseResetModal = () => {
		if (this.state.resetSubmitting) return;
		this.update({ resetModalOpen: false, resetError: null });
	};

	onConfirmReset = async () => {
		const { selectedAccountId } = this.state;
		const { clientId } = this.props;
		if (!selectedAccountId) return;
		await this.update({ resetSubmitting: true, resetError: null });

		const result = await LoanReconciliationService.resetSchedule({
			clientId,
			loanAccountId: selectedAccountId
		});

		if (result && result.error) {
			await this.update({ resetSubmitting: false, resetError: result.error });
			return;
		}

		// Clear local schedule state so the setup screen (upload / generate)
		// renders. We don't reload from server because the schedule is now
		// gone — there's nothing to load.
		await this.update({
			schedule: null,
			scheduleRows: [],
			scheduleHistory: [],
			resetSubmitting: false,
			resetModalOpen: false,
			expandedRowId: null,
			yearViewActive: false,
			yearOverview: null
		});
	};

	onToggleYearView = async () => {
		const next = !this.state.yearViewActive;
		if (next) {
			const { clientId } = this.props;
			const { selectedAccountId, yearViewYear } = this.state;
			const yo = await LoanReconciliationService.getYearOverview({
				clientId,
				loanAccountId: selectedAccountId,
				year: yearViewYear
			});
			await this.update({ yearViewActive: true, yearOverview: yo });
		} else {
			await this.update({ yearViewActive: false });
		}
	};

	onYearChange = async e => {
		const yr = parseInt(e.target.value) || new Date().getFullYear();
		await this.update({ yearViewYear: yr });
		if (this.state.yearViewActive) {
			const { clientId } = this.props;
			const { selectedAccountId } = this.state;
			const yo = await LoanReconciliationService.getYearOverview({
				clientId,
				loanAccountId: selectedAccountId,
				year: yr
			});
			await this.update({ yearOverview: yo });
		}
	};

	onToggleRowDetail = id => this.update({ expandedRowId: this.state.expandedRowId === id ? null : id });

	// ── Schedule render ──

	renderScheduleSetup() {
		const {
			scheduleMode,
			scheduleFile,
			genLoanAmount,
			genInterestRate,
			genTermMonths,
			genFirstPaymentDate,
			genPaymentFrequency,
			scheduleLoading,
			scheduleError
		} = this.state;

		return (
			<div className="loan-schedule__setup">
				<div className="loan-schedule__setup-toggle">
					<button
						className={`loan-schedule__toggle-btn ${scheduleMode === "upload" ? "loan-schedule__toggle-btn--active" : ""}`}
						onClick={() => this.onScheduleModeChange("upload")}
					>
						Upload
					</button>
					<button
						className={`loan-schedule__toggle-btn ${scheduleMode === "generate" ? "loan-schedule__toggle-btn--active" : ""}`}
						onClick={() => this.onScheduleModeChange("generate")}
					>
						Auto-generate
					</button>
				</div>

				{scheduleMode === "upload" ? (
					<div className="loan-schedule__upload-zone">
						<p className="loan-schedule__hint">Upload a bank-provided amortization schedule. Accepted: PDF, XLSX, CSV.</p>
						<label className="bank-recon-tool__file-btn" htmlFor="schedule-upload">
							{scheduleFile ? "Change File" : "Choose File"}
							<input
								id="schedule-upload"
								type="file"
								accept=".pdf,.xlsx,.xls,.csv"
								onChange={this.onScheduleFileChange}
								style={{ display: "none" }}
							/>
						</label>
						{scheduleFile && (
							<span className="loan-schedule__file-name">{scheduleFile.name}</span>
						)}
						<Button
							text={scheduleLoading ? "Importing..." : "Import"}
							onClick={this.onUploadSchedule}
							disabled={scheduleLoading || !scheduleFile}
						/>
					</div>
				) : (
					<div className="loan-schedule__gen-form">
						<div className="loan-schedule__gen-row">
							<label>Loan Amount</label>
							<input
								type="number"
								value={genLoanAmount}
								onChange={e => this.update({ genLoanAmount: e.target.value })}
								placeholder="60000"
							/>
						</div>
						<div className="loan-schedule__gen-row">
							<label>Interest Rate (%)</label>
							<input
								type="number"
								step="0.01"
								value={genInterestRate}
								onChange={e => this.update({ genInterestRate: e.target.value })}
								placeholder="4.85"
							/>
						</div>
						<div className="loan-schedule__gen-row">
							<label>Term (months)</label>
							<input
								type="number"
								value={genTermMonths}
								onChange={e => this.update({ genTermMonths: e.target.value })}
								placeholder="60"
							/>
						</div>
						<div className="loan-schedule__gen-row">
							<DateSelector
								title="First Payment Date"
								selectedDate={genFirstPaymentDate ? new Date(genFirstPaymentDate) : undefined}
								onDateSelect={d => this.update({ genFirstPaymentDate: d })}
							/>
						</div>
						<div className="loan-schedule__gen-row">
							<label>Frequency</label>
							<select
								value={genPaymentFrequency}
								onChange={e => this.update({ genPaymentFrequency: e.target.value })}
							>
								<option value="monthly">Monthly</option>
								<option value="biweekly">Biweekly</option>
								<option value="weekly">Weekly</option>
							</select>
						</div>
						<Button
							text={scheduleLoading ? "Generating..." : "Generate"}
							onClick={this.onGenerateSchedule}
							disabled={scheduleLoading}
						/>
					</div>
				)}

				{scheduleError && <div className="bank-recon-tool__error">{scheduleError}</div>}
			</div>
		);
	}

	renderScheduleMetrics() {
		const { scheduleRows, schedule } = this.state;
		// Any row where the borrower actually made a payment counts as paid:
		// PAID (exact match), EXTRA_PAID (more than scheduled), VARIANCE (total
		// matches but split is off), and PARTIAL (less than scheduled but cash
		// did move). Only "missed" / "scheduled" / "deferred" / "cancelled" are
		// excluded.
		const settledStatuses = ["paid", "extra_paid", "variance", "partial"];
		const settledRows = scheduleRows.filter(r => settledStatuses.includes(r.status));
		const paidCount = settledRows.length;
		const missedCount = scheduleRows.filter(r => r.status === "missed").length;
		const total = scheduleRows.length;

		// Remaining balance is computed from ACTUAL principal applied across
		// every settled row, not from the per-row `remaining_balance` field
		// (which was frozen at schedule-generation time using SCHEDULED splits
		// — that diverges from reality the moment a payment lands with a
		// different principal/interest breakdown, or a row gets missed).
		// Falling back to the original loan amount keeps the metric sane when
		// no payments have been recorded yet.
		const originalLoanAmount = schedule
			? parseFloat(schedule.loan_amount || 0)
			: scheduleRows.length > 0
			? parseFloat(scheduleRows[0].scheduled_principal) +
			  parseFloat(scheduleRows[0].remaining_balance)
			: 0;
		const totalActualPrincipal = settledRows.reduce(
			(sum, r) => sum + parseFloat(r.actual_principal || 0),
			0
		);
		const remainingBalance = settledRows.length > 0
			? parseFloat((originalLoanAmount - totalActualPrincipal).toFixed(2))
			: originalLoanAmount;
		const nextDue = scheduleRows.find(r => r.status === "scheduled");

		return (
			<div className="loan-schedule__metrics">
				<div className="loan-schedule__metric">
					<div className="loan-schedule__metric-label">Paid</div>
					<div className="loan-schedule__metric-value">{paidCount} of {total}</div>
				</div>
				<div className={`loan-schedule__metric ${missedCount > 0 ? "loan-schedule__metric--bad" : ""}`}>
					<div className="loan-schedule__metric-label">Missed</div>
					<div className="loan-schedule__metric-value">{missedCount}</div>
				</div>
				<div className="loan-schedule__metric">
					<div className="loan-schedule__metric-label">Remaining Balance</div>
					<div className="loan-schedule__metric-value">{this.formatCurrency(remainingBalance)}</div>
				</div>
				<div className="loan-schedule__metric">
					<div className="loan-schedule__metric-label">Next Due</div>
					<div className="loan-schedule__metric-value">
						{nextDue ? moment(nextDue.due_date).format("MMM D") : "—"}
					</div>
				</div>
			</div>
		);
	}

	renderScheduleRow(row) {
		const { expandedRowId } = this.state;
		const isExpanded = expandedRowId === row.id;
		const variance = row.variance != null ? parseFloat(row.variance) : null;
		const varianceClass =
			variance == null || Math.abs(variance) < 0.005
				? "loan-schedule__variance--neutral"
				: variance > 0
				? "loan-schedule__variance--pos"
				: "loan-schedule__variance--neg";

		return (
			<React.Fragment key={row.id}>
				<tr
					className={`loan-schedule__row loan-schedule__row--${row.status}`}
					onClick={() => this.onToggleRowDetail(row.id)}
				>
					<td>{row.payment_number}</td>
					<td>{row.due_date}</td>
					<td>{this.formatCurrency(parseFloat(row.scheduled_payment))}</td>
					<td>{row.actual_amount != null ? this.formatCurrency(parseFloat(row.actual_amount)) : "—"}</td>
					<td className={varianceClass}>
						{variance != null ? this.formatCurrency(variance) : "—"}
					</td>
					<td>
						<span className={`loan-schedule__badge loan-schedule__badge--${row.status}`}>
							{STATUS_LABELS[row.status] || row.status}
						</span>
					</td>
				</tr>
				{isExpanded && (
					<tr className="loan-schedule__row-detail">
						<td colSpan={6}>{this.renderRowDetail(row)}</td>
					</tr>
				)}
			</React.Fragment>
		);
	}

	renderRowDetail(row) {
		const { scheduleRows, schedule } = this.state;
		const scheduledP = parseFloat(row.scheduled_principal);
		const scheduledI = parseFloat(row.scheduled_interest);
		const actualP = row.actual_principal != null ? parseFloat(row.actual_principal) : null;
		const actualI = row.actual_interest != null ? parseFloat(row.actual_interest) : null;

		// Schedule's projected balance after this row — already stored on the
		// row by the amortization formula at generation time.
		const scheduleBalance = parseFloat(row.remaining_balance);

		// Actual (bookkeeper) balance after this row: original loan amount
		// minus the sum of actual principal applied through this row across
		// every settled row (paid / extra_paid / variance / partial). Rows
		// without an actual_principal contribute 0 — so missed/scheduled
		// rows don't move the balance, which mirrors what's in QBO.
		const settledStatuses = ["paid", "extra_paid", "variance", "partial"];
		const originalLoanAmount = schedule
			? parseFloat(schedule.loan_amount || 0)
			: scheduleRows.length > 0
			? parseFloat(scheduleRows[0].scheduled_principal) +
			  parseFloat(scheduleRows[0].remaining_balance)
			: 0;
		const actualBalance = originalLoanAmount -
			scheduleRows
				.filter(r => r.payment_number <= row.payment_number && settledStatuses.includes(r.status))
				.reduce((sum, r) => sum + parseFloat(r.actual_principal || 0), 0);

		return (
			<div className="loan-schedule__detail">
				<div className="loan-schedule__detail-cols">
					<div className="loan-schedule__detail-col">
						<div className="loan-schedule__detail-title">Schedule says</div>
						<div className="loan-schedule__detail-line">Principal: <strong>{this.formatCurrency(scheduledP)}</strong></div>
						<div className="loan-schedule__detail-line">Interest: <strong>{this.formatCurrency(scheduledI)}</strong></div>
						<div className="loan-schedule__detail-line">Total: <strong>{this.formatCurrency(parseFloat(row.scheduled_payment))}</strong></div>
						<div className="loan-schedule__detail-line loan-schedule__detail-balance">Balance after: <strong>{this.formatCurrency(scheduleBalance)}</strong></div>
					</div>
					<div className={`loan-schedule__detail-col ${row.status === "variance" ? "loan-schedule__detail-col--problem" : ""}`}>
						<div className="loan-schedule__detail-title">Bookkeeper booked</div>
						{actualP != null ? (
							<>
								<div className="loan-schedule__detail-line">Principal: <strong>{this.formatCurrency(actualP)}</strong></div>
								<div className="loan-schedule__detail-line">Interest: <strong>{this.formatCurrency(actualI || 0)}</strong></div>
								<div className="loan-schedule__detail-line">Total: <strong>{this.formatCurrency(actualP + (actualI || 0))}</strong></div>
							</>
						) : (
							<div className="loan-schedule__detail-line">No QBO entry yet.</div>
						)}
						<div className="loan-schedule__detail-line loan-schedule__detail-balance">Balance after: <strong>{this.formatCurrency(actualBalance)}</strong></div>
					</div>
				</div>

				{row.status === "variance" && (
					<div className="loan-schedule__warning-banner">
						Variance detected on {row.due_date}. Schedule expected{" "}
						{this.formatCurrency(scheduledP)} principal / {this.formatCurrency(scheduledI)} interest;
						bookkeeper booked {this.formatCurrency(actualP || 0)} / {this.formatCurrency(actualI || 0)}.
					</div>
				)}

				{row.status === "missed" && (
					<div className="loan-schedule__warning-banner loan-schedule__warning-banner--bad">
						Missed payment for {row.due_date}. Suggested JE shown in the Suggested JEs tab.
					</div>
				)}

				{row.status === "partial" && (
					<div className="loan-schedule__warning-banner">
						Partial payment on {row.due_date}. Booked {this.formatCurrency(parseFloat(row.actual_amount))} vs scheduled {this.formatCurrency(parseFloat(row.scheduled_payment))}. Shortfall carries to next reconciliation.
					</div>
				)}

				{row.status === "extra_paid" && (() => {
					const alreadyRecalculated =
						row.notes && row.notes.includes("Extra payment recalculated forward");
					return (
						<div className="loan-schedule__warning-banner loan-schedule__warning-banner--good">
							Extra payment of {this.formatCurrency(parseFloat(row.actual_amount) - parseFloat(row.scheduled_payment))} detected.{" "}
							{alreadyRecalculated ? (
								<span className="loan-schedule__recalc-done">✓ Forward rows recalculated</span>
							) : (
								<button
									className="loan-schedule__inline-btn"
									onClick={() => this.onOpenExtraPayment(row.id)}
								>
									Recalculate forward rows
								</button>
							)}
						</div>
					);
				})()}

				<div className="loan-schedule__detail-actions">
					{row.status === "deferred" ? (
						<button className="loan-schedule__inline-btn" onClick={() => this.onRevertRowToScheduled(row.id)}>
							Revert to scheduled
						</button>
					) : row.status === "scheduled" ? (
						<button className="loan-schedule__inline-btn" onClick={() => this.onMarkRowDeferred(row.id)}>
							Mark as deferred
						</button>
					) : null}
				</div>

				{row.notes && <div className="loan-schedule__notes">Note: {row.notes}</div>}
			</div>
		);
	}

	renderYearView() {
		const { yearOverview, yearViewYear } = this.state;
		if (!yearOverview) return null;
		const { months = [], totals = {} } = yearOverview;

		return (
			<div className="loan-schedule__year-view">
				<div className="loan-schedule__metrics">
					<div className="loan-schedule__metric">
						<div className="loan-schedule__metric-label">Paid On Time</div>
						<div className="loan-schedule__metric-value">{totals.paidOnTime || 0}</div>
					</div>
					<div className="loan-schedule__metric">
						<div className="loan-schedule__metric-label">Late / Partial</div>
						<div className="loan-schedule__metric-value">{totals.lateOrPartial || 0}</div>
					</div>
					<div className="loan-schedule__metric loan-schedule__metric--bad">
						<div className="loan-schedule__metric-label">Missed</div>
						<div className="loan-schedule__metric-value">{totals.missed || 0}</div>
					</div>
					<div className="loan-schedule__metric">
						<div className="loan-schedule__metric-label">Extra Payments</div>
						<div className="loan-schedule__metric-value">{totals.extraPayments || 0}</div>
					</div>
				</div>

				<table className="bank-recon-items__table">
					<thead>
						<tr>
							<th>Month</th>
							<th>Status</th>
							<th>Variance</th>
							<th>Notes</th>
						</tr>
					</thead>
					<tbody>
						{months.length === 0 ? (
							<tr>
								<td colSpan={4}>No payments scheduled for {yearViewYear}.</td>
							</tr>
						) : (
							months.map(m => (
								<tr key={m.month}>
									<td>{moment(m.month, "YYYY-MM").format("MMMM YYYY")}</td>
									<td>
										<span className={`loan-schedule__badge loan-schedule__badge--${m.status}`}>
											{STATUS_LABELS[m.status] || m.status}
										</span>
									</td>
									<td>{m.variance ? this.formatCurrency(m.variance) : "—"}</td>
									<td>{m.notes || "—"}</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
		);
	}

	renderRateChangeModal() {
		const { rateChangeModalOpen, rateChangeEffectiveDate, rateChangeNewRate } = this.state;
		if (!rateChangeModalOpen) return null;
		return (
			<div className="loan-schedule__modal-backdrop" onClick={this.onCloseRateChangeModal}>
				<div className="loan-schedule__modal" onClick={e => e.stopPropagation()}>
					<div className="loan-schedule__modal-header">
						<h3>Record Rate Change</h3>
						<span className="loan-schedule__modal-close" onClick={this.onCloseRateChangeModal}>&times;</span>
					</div>
					<div className="loan-schedule__modal-body">
						<DateSelector
							title="Effective Date"
							selectedDate={rateChangeEffectiveDate ? new Date(rateChangeEffectiveDate) : undefined}
							onDateSelect={d => this.update({ rateChangeEffectiveDate: d })}
						/>
						<div className="loan-schedule__gen-row">
							<label>New Interest Rate (%)</label>
							<input
								type="number"
								step="0.01"
								value={rateChangeNewRate}
								onChange={e => this.update({ rateChangeNewRate: e.target.value })}
								placeholder="5.50"
							/>
						</div>
					</div>
					<div className="loan-schedule__modal-footer">
						<Button text="Cancel" onClick={this.onCloseRateChangeModal} />
						<Button text="Apply" onClick={this.onSubmitRateChange} />
					</div>
				</div>
			</div>
		);
	}

	renderExtraPaymentModal() {
		const { extraPaymentModalOpen, extraPaymentMode } = this.state;
		if (!extraPaymentModalOpen) return null;
		return (
			<div className="loan-schedule__modal-backdrop" onClick={this.onCloseExtraPayment}>
				<div className="loan-schedule__modal" onClick={e => e.stopPropagation()}>
					<div className="loan-schedule__modal-header">
						<h3>Recalculate after extra payment</h3>
						<span className="loan-schedule__modal-close" onClick={this.onCloseExtraPayment}>&times;</span>
					</div>
					<div className="loan-schedule__modal-body">
						<label className="loan-schedule__option">
							<input
								type="radio"
								name="extraMode"
								value="shorten"
								checked={extraPaymentMode === "shorten"}
								onChange={() => this.update({ extraPaymentMode: "shorten" })}
							/>
							<strong>Shorten the loan</strong> — keep the same payment, end the loan early.
						</label>
						<label className="loan-schedule__option">
							<input
								type="radio"
								name="extraMode"
								value="keep_term"
								checked={extraPaymentMode === "keep_term"}
								onChange={() => this.update({ extraPaymentMode: "keep_term" })}
							/>
							<strong>Keep the term</strong> — lower remaining payments, original count.
						</label>
					</div>
					<div className="loan-schedule__modal-footer">
						<Button text="Cancel" onClick={this.onCloseExtraPayment} />
						<Button text="Apply" onClick={this.onSubmitExtraPayment} />
					</div>
				</div>
			</div>
		);
	}

	renderReplaceModal() {
		const {
			replaceModalOpen,
			replaceFile,
			replaceFromDate,
			replaceSubmitting,
			replaceError
		} = this.state;
		if (!replaceModalOpen) return null;
		return (
			<div className="loan-schedule__modal-backdrop" onClick={this.onCloseReplace}>
				<div className="loan-schedule__modal" onClick={e => e.stopPropagation()}>
					<div className="loan-schedule__modal-header">
						<h3>Replace Schedule (Refinancing)</h3>
						{!replaceSubmitting && (
							<span className="loan-schedule__modal-close" onClick={this.onCloseReplace}>&times;</span>
						)}
					</div>
					<div className="loan-schedule__modal-body">
						<DateSelector
							title="Replace From Date"
							selectedDate={replaceFromDate ? new Date(replaceFromDate) : undefined}
							onDateSelect={d => this.update({ replaceFromDate: d })}
							disabled={replaceSubmitting}
						/>
						<label
							className={`bank-recon-tool__file-btn ${replaceSubmitting ? "is-disabled" : ""}`}
							htmlFor="replace-upload"
						>
							{replaceFile ? "Change File" : "Choose File"}
							<input
								id="replace-upload"
								type="file"
								accept=".pdf,.xlsx,.xls,.csv"
								onChange={this.onReplaceFileChange}
								style={{ display: "none" }}
								disabled={replaceSubmitting}
							/>
						</label>
						{replaceFile && <div className="loan-schedule__file-name">{replaceFile.name}</div>}

						{replaceSubmitting && (
							<div className="loan-schedule__replace-status">
								<span className="loan-schedule__spinner" />
								<span>Parsing schedule and rebuilding rows — this can take 10–20 seconds…</span>
							</div>
						)}

						{replaceError && !replaceSubmitting && (
							<div className="loan-schedule__replace-error">
								<strong>Could not replace schedule.</strong>
								<div>{replaceError}</div>
								<div className="loan-schedule__replace-error-hint">
									Double-check the file format and that the dates / amounts parse cleanly. The original schedule is unchanged.
								</div>
							</div>
						)}
					</div>
					<div className="loan-schedule__modal-footer">
						<Button
							text="Cancel"
							onClick={this.onCloseReplace}
							disabled={replaceSubmitting}
						/>
						<Button
							text={replaceSubmitting ? "Applying…" : "Apply Replacement"}
							onClick={this.onSubmitReplace}
							disabled={replaceSubmitting || !replaceFile}
						/>
					</div>
				</div>
			</div>
		);
	}

	renderResetModal() {
		const { resetModalOpen, resetSubmitting, resetError, scheduleRows } = this.state;
		if (!resetModalOpen) return null;
		const rowCount = scheduleRows ? scheduleRows.length : 0;
		return (
			<div className="loan-schedule__modal-backdrop" onClick={this.onCloseResetModal}>
				<div className="loan-schedule__modal" onClick={e => e.stopPropagation()}>
					<div className="loan-schedule__modal-header loan-schedule__modal-header--danger">
						<h3>Return schedule to default?</h3>
						{!resetSubmitting && (
							<span className="loan-schedule__modal-close" onClick={this.onCloseResetModal}>&times;</span>
						)}
					</div>
					<div className="loan-schedule__modal-body">
						<div className="loan-schedule__reset-warning">
							<div className="loan-schedule__reset-warning-icon">!</div>
							<div>
								<strong>This will permanently delete every schedule on this loan account.</strong>
								<ul className="loan-schedule__reset-bullets">
									<li>{rowCount} schedule row{rowCount === 1 ? "" : "s"} will be removed</li>
									<li>All rate changes, extra-payment recalcs, and refinancing history will be lost</li>
									<li>Past reconciliations and journal entries in QBO will <strong>not</strong> be touched</li>
								</ul>
								<div className="loan-schedule__reset-after">
									After reset, you'll be able to upload a new amortization schedule or auto-generate one from loan terms.
								</div>
							</div>
						</div>

						{resetError && !resetSubmitting && (
							<div className="loan-schedule__replace-error">
								<strong>Could not reset schedule.</strong>
								<div>{resetError}</div>
							</div>
						)}
					</div>
					<div className="loan-schedule__modal-footer">
						<Button
							text="Cancel"
							onClick={this.onCloseResetModal}
							disabled={resetSubmitting}
						/>
						<Button
							text={resetSubmitting ? "Resetting…" : "Yes, delete everything"}
							onClick={this.onConfirmReset}
							disabled={resetSubmitting}
							className="loan-schedule__btn--danger"
						/>
					</div>
				</div>
			</div>
		);
	}

	renderSchedule() {
		const {
			schedule,
			scheduleRows,
			scheduleLoading,
			selectedAccountId,
			yearViewActive,
			yearViewYear
		} = this.state;

		if (!selectedAccountId) {
			return (
				<div className="loan-schedule">
					<p>Select a loan account first.</p>
				</div>
			);
		}

		if (scheduleLoading && !schedule) {
			return (
				<div className="loan-schedule">
					{this.renderScheduleImportProgress()}
				</div>
			);
		}

		if (!schedule) {
			return (
				<div className="loan-schedule">
					<h3>Set up an amortization schedule</h3>
					{this.renderScheduleSetup()}
					{this.renderRateChangeModal()}
					{this.renderExtraPaymentModal()}
					{this.renderReplaceModal()}
				</div>
			);
		}

		return (
			<div className="loan-schedule">
				{this.state.replaceSuccessToast && (
					<div className="loan-schedule__toast loan-schedule__toast--success">
						<span className="loan-schedule__toast-icon">✓</span>
						<span className="loan-schedule__toast-msg">{this.state.replaceSuccessToast}</span>
						<button
							className="loan-schedule__toast-close"
							onClick={this.onDismissReplaceToast}
							aria-label="Dismiss"
						>
							&times;
						</button>
					</div>
				)}
				<div className="loan-schedule__header">
					<h3>Amortization Schedule</h3>
					<div className="loan-schedule__header-actions">
						<button className="loan-schedule__inline-btn" onClick={this.onOpenRateChangeModal}>
							Record rate change
						</button>
						<button className="loan-schedule__inline-btn" onClick={this.onOpenReplace}>
							Replace schedule
						</button>
						<button className="loan-schedule__inline-btn" onClick={this.onToggleYearView}>
							{yearViewActive ? "Schedule view" : "Year view"}
						</button>
						<button
							className="loan-schedule__inline-btn loan-schedule__inline-btn--danger"
							onClick={this.onOpenResetModal}
						>
							Return to default
						</button>
						{yearViewActive && (
							<input
								type="number"
								className="loan-schedule__year-input"
								value={yearViewYear}
								onChange={this.onYearChange}
							/>
						)}
					</div>
				</div>

				{yearViewActive ? (
					this.renderYearView()
				) : (
					<>
						{this.renderScheduleMetrics()}
						<table className="bank-recon-items__table loan-schedule__table">
							<thead>
								<tr>
									<th>#</th>
									<th>Date</th>
									<th>Scheduled</th>
									<th>Actual</th>
									<th>Variance</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>{scheduleRows.map(r => this.renderScheduleRow(r))}</tbody>
						</table>
					</>
				)}

				{this.renderRateChangeModal()}
				{this.renderExtraPaymentModal()}
				{this.renderReplaceModal()}
				{this.renderResetModal()}
			</div>
		);
	}

	renderActiveTab() {
		const { activeTab, reconciliationResult } = this.state;

		if (activeTab === TABS.history) return this.renderHistory();
		if (activeTab === TABS.schedule) return this.renderSchedule();
		if (!reconciliationResult) return null;

		switch (activeTab) {
			case TABS.summary:
				return this.renderSummary();
			case TABS.statementOnly:
				return this.renderItemsTable(
					reconciliationResult.unmatchedStatementItems,
					"Statement-Only Items (in lender statement, not in GL)",
					"statement"
				);
			case TABS.glOnly:
				return this.renderItemsTable(
					reconciliationResult.unmatchedGLItems,
					"GL-Only Items (in books, not on lender statement)",
					"gl"
				);
			case TABS.journalEntries:
				return this.renderJournalEntries();
			default:
				return this.renderSummary();
		}
	}

	render() {
		const { loading, error, clientName } = this.state;

		return (
			<div className="container">
				<div className="container-header-title">
					<h1 className="container-header-title__name">
						Loan Reconciliation{clientName ? ` — ${clientName}` : ""}
					</h1>
				</div>

				<div className="bank-recon-tool__body">
					{this.renderControls()}

					{error && <div className="bank-recon-tool__error">{error}</div>}

					{loading ? (
						this.renderReconcileProgress()
					) : (
						<>
							{this.renderTabs()}
							{this.renderActiveTab()}
						</>
					)}
				</div>
			</div>
		);
	}
}

export default LoanReconciliationTool;
